import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button, IconButton, Tooltip } from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

// ── motion ─────────────────────────────────────────────────────────────────────
const fadeIn     = { hidden:{opacity:0}, visible:{opacity:1,transition:{duration:0.5}} };
const leftEntry  = { hidden:{x:-40,opacity:0}, visible:{x:0,opacity:1,transition:{duration:0.5}} };
const rightEntry = { hidden:{x:40,opacity:0},  visible:{x:0,opacity:1,transition:{duration:0.5}} };
const lineEntry  = {
  hidden:{opacity:0,y:6},
  visible:(i)=>({opacity:1,y:0,transition:{duration:0.2,delay:Math.min(i,20)*0.02}}),
};

// ── constants ──────────────────────────────────────────────────────────────────
const API_URL  = "https://stdiscount.bond/admin/api/logs";
const TOP_H    = 48;
const FOOTER_H = 34;
const GRAD     = "linear-gradient(to bottom, #8018e7, #10b981, #3b82f6)";
const BAR_T    = 5;
const BAR_V    = 3.5;

const LEVEL = {
  error: { c:"#ef4444", bg:"rgba(239,68,68,0.12)",  b:"rgba(239,68,68,0.35)" },
  warn:  { c:"#fbbf24", bg:"rgba(251,191,36,0.11)", b:"rgba(251,191,36,0.35)" },
  info:  { c:"#60a5fa", bg:"rgba(59,130,246,0.10)", b:"rgba(59,130,246,0.30)" },
  debug: { c:"#94a3b8", bg:"rgba(148,163,184,0.09)",b:"rgba(148,163,184,0.25)" },
};
const lvl = (l) => LEVEL[l] || LEVEL.debug;

const PROCESS_COLOR = {
  inventory_sync: "#10b981",
  order_event:    "#a855f7",
  cache:          "#2dd4bf",
  system:         "#94a3b8",
};

const STATUS = {
  ok:      { c:"#10b981", bg:"rgba(16,185,129,0.14)", b:"rgba(16,185,129,0.40)", label:"complete" },
  error:   { c:"#ef4444", bg:"rgba(239,68,68,0.12)",  b:"rgba(239,68,68,0.35)",  label:"error" },
  stuck:   { c:"#fbbf24", bg:"rgba(251,191,36,0.11)", b:"rgba(251,191,36,0.35)", label:"no completion" },
  partial: { c:"#94a3b8", bg:"rgba(148,163,184,0.09)",b:"rgba(148,163,184,0.25)",label:"fragment" },
};

const FETCH_LIMIT = 150000;

const LEVELS    = ["all","info","warn","error","debug"];
const PROCESSES = ["all","inventory_sync","order_event","cache","system"];
const DAY_OPTS  = [1,3,7,30];

// ── helpers ────────────────────────────────────────────────────────────────────
const ts = (r) => new Date(r.created_at).getTime();

const fmtClock = (ms) =>
  Number.isFinite(ms)
    ? new Date(ms).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit", second:"2-digit" })
    : "—";

const fmtDate = (ms) =>
  Number.isFinite(ms) ? new Date(ms).toLocaleDateString([], { day:"2-digit", month:"2-digit" }) : "—";

const fmtDur = (ms) => {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms/1000).toFixed(1)}s`;
  const m = Math.floor(ms/60000), s = Math.round((ms%60000)/1000);
  return `${m}m ${s}s`;
};

function groupRuns(logs = [], network = []) {
  const map = new Map();
  const bucket = (id) => {
    const k = id || "__unlinked__";
    if (!map.has(k)) map.set(k, { runId:k, unlinked: !id, logs:[], network:[] });
    return map.get(k);
  };

  logs.forEach(l => bucket(l.run_id).logs.push(l));
  network.forEach(n => bucket(n.run_id).network.push(n));

  const runs = [...map.values()].map(r => {
    const logsAsc = [...r.logs].sort((a,b) => ts(a) - ts(b));
    const netAsc  = [...r.network].sort((a,b) => ts(a) - ts(b));
    const stamps  = [...logsAsc, ...netAsc].map(ts).filter(Number.isFinite);
    const start   = stamps.length ? Math.min(...stamps) : NaN;
    const end     = stamps.length ? Math.max(...stamps) : NaN;

    const hasComplete  = logsAsc.some(l => l.details?.stage === "complete");
    const hasHeartbeat = logsAsc.some(l => l.details?.stage === "heartbeat");
    const errorCount   = logsAsc.filter(l => l.level === "error").length
                       + netAsc.filter(n => n.success === false).length;

    let status = "partial";
    if (errorCount) status = "error";
    else if (hasComplete) status = "ok";
    else if (hasHeartbeat) status = "stuck";

    const processes = [...new Set(logsAsc.map(l => l.process).filter(Boolean))];
    const trigger   = logsAsc.find(l => l.details?.trigger)?.details?.trigger || null;

    return {
      ...r, logs:logsAsc, network:netAsc,
      start, end, duration:end - start,
      status, errorCount, processes, trigger,
    };
  });

  runs.sort((a,b) => (b.start || 0) - (a.start || 0));
  return runs;
}

// ── gradient scrollbar (same treatment as Scene Builder) ───────────────────────
const GradientBar = ({ scrollEl }) => {
  const [thumb, setThumb]     = useState({ pos:0, size:0 });
  const [visible, setVisible] = useState(false);
  const fadeTimer             = useRef(null);

  useEffect(() => {
    const el = scrollEl.current;
    if (!el) return;
    const update = () => {
      if (el.scrollHeight <= el.clientHeight) { setVisible(false); return; }
      const ratio = el.clientHeight / el.scrollHeight;
      const pos   = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * (el.clientHeight * (1 - ratio));
      setThumb({ pos, size: el.clientHeight * ratio });
      setVisible(true);
    };
    el.addEventListener("scroll", update);
    update();
    return () => { el.removeEventListener("scroll", update); clearTimeout(fadeTimer.current); };
  }, [scrollEl]);

  return (
    <div style={{
      position:"absolute", top:0, right:0, bottom:0, width:BAR_V,
      pointerEvents:"none", zIndex:20,
      opacity: visible ? 0.45 : 0, transition:"opacity 0.6s",
    }}>
      <div style={{
        position:"absolute", right:0, width:BAR_V,
        top:thumb.pos, height:thumb.size,
        background:GRAD, borderRadius:BAR_T,
      }}/>
    </div>
  );
};

// ── small UI pieces ────────────────────────────────────────────────────────────
const Pill = ({ text, c, bg, b, title }) => (
  <span title={title} style={{
    fontSize:10, fontWeight:700, fontFamily:"'Barlow',sans-serif",
    letterSpacing:"0.07em", textTransform:"uppercase",
    padding:"3px 9px", flexShrink:0,
    background:bg, border:`1px solid ${b}`, color:c,
  }}>{text}</span>
);

const tabBtnSx = (active) => ({
  fontFamily:"'Barlow',sans-serif", fontSize:13, fontWeight: active ? 600 : 400,
  letterSpacing:"0.06em", textTransform:"none", minWidth:0,
  px:1.6, py:0.6, borderRadius:0,
  borderBottom: active ? "2px solid #10b981" : "2px solid transparent",
  color: active ? "#10b981" : "rgba(255,255,255,0.35)",
  "&:hover":{ color:"rgba(255,255,255,0.75)", borderColor:"transparent" },
});

const ChipBtn = ({ label, active, onClick }) => (
  <button onClick={onClick} style={{
    fontFamily:"'Barlow',sans-serif", fontSize:11, fontWeight: active ? 600 : 400,
    letterSpacing:"0.07em", textTransform:"uppercase", padding:"3px 11px",
    border:`1px solid ${active ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.14)"}`,
    background: active ? "rgba(255,255,255,0.08)" : "transparent",
    color: active ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.33)",
    cursor:"pointer", transition:"all 0.2s ease", outline:"none",
  }}>{label}</button>
);

const Metric = ({ label, value, color }) => (
  <div style={{ display:"flex", flexDirection:"column", gap:2, minWidth:78 }}>
    <span style={{
      fontSize:10, letterSpacing:"0.09em", textTransform:"uppercase",
      color:"rgba(255,255,255,0.25)", fontFamily:"'Barlow',sans-serif", fontWeight:500,
    }}>{label}</span>
    <span style={{
      fontSize:16, fontFamily:"'Barlow',sans-serif", fontWeight:600,
      color: color || "rgba(255,255,255,0.85)", lineHeight:1.1,
    }}>{value}</span>
  </div>
);

// details object → compact key:value chips
const DetailChips = ({ details }) => {
  if (!details || typeof details !== "object") return null;
  const entries = Object.entries(details).filter(([,v]) => v !== null && v !== undefined);
  if (!entries.length) return null;
  return (
    <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginTop:5 }}>
      {entries.map(([k,v]) => (
        <span key={k} style={{
          fontSize:11, fontFamily:"monospace", padding:"2px 7px",
          background:"rgba(255,255,255,0.035)", border:"1px solid rgba(255,255,255,0.07)",
          color:"rgba(255,255,255,0.45)",
        }}>
          {k}<span style={{ color:"rgba(255,255,255,0.22)" }}>:</span>{" "}
          <span style={{ color:"#86efac" }}>
            {typeof v === "object" ? JSON.stringify(v) : String(v)}
          </span>
        </span>
      ))}
    </div>
  );
};

// ── PAGE ───────────────────────────────────────────────────────────────────────
const SyncLogs = () => {
  const [apiKey,      setApiKey]      = useState("");
  const [showKey,     setShowKey]     = useState(false);
  const [days,        setDays]        = useState(1);
  const [data,        setData]        = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [fetchedAt,   setFetchedAt]   = useState(null);
  const [payloadSize, setPayloadSize] = useState(null);

  const [selectedRun, setSelectedRun] = useState(null);
  const [levelFilter, setLevelFilter] = useState("all");
  const [procFilter,  setProcFilter]  = useState("all");
  const [view,        setView]        = useState("split"); // split | runs | detail
  const [copied,      setCopied]      = useState(false);

  const listRef   = useRef(null);
  const detailRef = useRef(null);


  const handleFetch = useCallback(async () => {
    const key = apiKey.trim();
    if (!key) { setError("Paste the admin key before loading."); return; }

    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}?days=${days}&limit=${FETCH_LIMIT}`, {
        headers: { "x-api-key": key },
      });
      if (res.status === 401) throw new Error("401 — that key was rejected.");
      if (!res.ok) throw new Error(`${res.status} ${res.statusText || "request failed"}`);
      const text = await res.text();
      setPayloadSize(text.length);
      const json = JSON.parse(text);
      setData(json);
      setFetchedAt(Date.now());
    } catch (e) {
      setError(
        e.message === "Failed to fetch"
          ? "Could not reach the API. Check the /admin CORS header on the droplet, or your connection."
          : e.message
      );
    } finally {
      setLoading(false);
    }
  }, [apiKey, days]);

    useEffect(() => {
    if (!data) return;
    handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  // ── derived ───────────────────────────────────────────────────────────────
  const runs = useMemo(
    () => (data ? groupRuns(data.logs, data.network) : []),
    [data]
  );

  const visibleRuns = useMemo(() => {
    const cutoff = Date.now() - days * 864e5;
    return runs.filter(r => {
      if (Number.isFinite(r.start) && r.start < cutoff) return false;
      if (procFilter !== "all" && !r.processes.includes(procFilter)) return false;
      if (levelFilter !== "all" && !r.logs.some(l => l.level === levelFilter)) return false;
      return true;
    });
  }, [runs, days, procFilter, levelFilter]);

  const active = useMemo(
    () => visibleRuns.find(r => r.runId === selectedRun) || null,
    [visibleRuns, selectedRun]
  );

  const activeLogs = useMemo(() => {
    if (!active) return [];
    return active.logs.filter(l =>
      (levelFilter === "all" || l.level === levelFilter) &&
      (procFilter  === "all" || l.process === procFilter)
    );
  }, [active, levelFilter, procFilter]);

  const health = useMemo(() => {
    if (!runs.length) return null;
    const dayStart = new Date().setHours(0,0,0,0);
    return {
      lastRun:   runs[0].start,
      runsToday: runs.filter(r => r.start >= dayStart).length,
      errors:    visibleRuns.reduce((n, r) => n + r.errorCount, 0),
      badRuns:   visibleRuns.filter(r => r.errorCount >= 3).length,
    };
  }, [runs, visibleRuns]);

  const copyRunId = useCallback(() => {
    if (!active) return;
    navigator.clipboard.writeText(active.runId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    });
  }, [active]);

  const midH = `calc(100% - ${TOP_H + FOOTER_H}px)`;

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ position:"relative", width:"100%", height:"100%", overflow:"hidden" }}>

      {/* ── TOP BAR ── */}
      <motion.div variants={fadeIn} initial="hidden" animate="visible" style={{
        position:"absolute", top:0, left:0, right:0, height:TOP_H,
        display:"flex", alignItems:"center", gap:8, padding:"0 20px",
        borderBottom:"1px solid rgba(255,255,255,0.07)", zIndex:10,
      }}>
        {["split","runs","detail"].map(v => (
          <Button key={v} onClick={() => setView(v)} sx={tabBtnSx(view === v)}>{v}</Button>
        ))}

        <div style={{ width:1, height:18, background:"rgba(255,255,255,0.1)", margin:"0 4px" }}/>

        {DAY_OPTS.map(d => (
          <ChipBtn key={d} label={d === 1 ? "24h" : `${d}d`} active={days === d} onClick={() => setDays(d)} />
        ))}

        <div style={{ flex:1 }}/>

        {/* key input */}
        <div style={{
          display:"flex", alignItems:"center",
          border:"1px solid rgba(255,255,255,0.14)", background:"rgba(0,0,0,0.2)",
          height:28, paddingLeft:10,
        }}>
          <input
            type={showKey ? "text" : "password"}
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") handleFetch(); }}
            placeholder="admin key"
            autoComplete="off"
            spellCheck={false}
            style={{
              background:"transparent", border:"none", outline:"none",
              color:"rgba(255,255,255,0.85)", fontFamily:"monospace", fontSize:12,
              width:190, letterSpacing: showKey ? "0" : "0.12em",
            }}
          />
          <IconButton onClick={() => setShowKey(s => !s)} size="small"
            sx={{ color:"rgba(255,255,255,0.25)", "&:hover":{ color:"#10b981" } }}>
            {showKey ? <VisibilityOffIcon sx={{ fontSize:15 }}/> : <VisibilityIcon sx={{ fontSize:15 }}/>}
          </IconButton>
        </div>

        <Button
          variant="outlined"
          startIcon={<PlayArrowIcon sx={{ fontSize:16 }}/>}
          onClick={handleFetch}
          disabled={loading}
          sx={{
            fontFamily:"'Barlow',sans-serif", fontSize:13, fontWeight:600,
            letterSpacing:"0.06em", textTransform:"none", px:2, py:0.5,
            borderColor:"rgba(156,163,175,0.5)", color:"rgba(255,255,255,0.78)", borderRadius:0,
            transition:"all 0.3s ease",
            "&.Mui-disabled":{ color:"rgba(255,255,255,0.25)", borderColor:"rgba(255,255,255,0.1)" },
            "&:hover":{ borderColor:"transparent", borderImage:"linear-gradient(to right,#8018e7,#10b981,#3b82f6) 1", color:"#fff" },
          }}
        >{loading ? "Loading" : data ? "Reload" : "Load logs"}</Button>
      </motion.div>

      {/* ── ERROR BAR ── */}
      <AnimatePresence>
        {error && (
          <motion.div initial={{ opacity:0 }} animate={{ opacity:1 }} exit={{ opacity:0 }} style={{
            position:"absolute", top:TOP_H, left:0, right:0, minHeight:32,
            padding:"7px 20px", display:"flex", alignItems:"center",
            background:"rgba(239,68,68,0.1)", borderBottom:"1px solid rgba(239,68,68,0.3)",
            fontSize:12, color:"#ef4444", fontFamily:"monospace", zIndex:11,
          }}>{error}</motion.div>
        )}
      </AnimatePresence>

      {/* ── MIDDLE ── */}
      <div style={{ position:"absolute", top:TOP_H, left:0, right:0, height:midH, display:"flex", overflow:"hidden" }}>

        {/* empty state */}
        {!data && !loading && (
          <div style={{
            flex:1, display:"flex", flexDirection:"column", alignItems:"center",
            justifyContent:"center", gap:10, padding:20,
          }}>
            <span style={{
              fontSize:15, fontFamily:"'Barlow',sans-serif", fontWeight:300,
              color:"rgba(255,255,255,0.5)",
            }}>Paste the admin key above and load the last {days === 1 ? "24 hours" : `${days} days`} of sync activity.</span>
            <span style={{ fontSize:12, fontFamily:"'Barlow',sans-serif", color:"rgba(255,255,255,0.22)" }}>
              The key stays in memory only — a page refresh clears it.
            </span>
          </div>
        )}

        {loading && (
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center" }}>
            <span style={{
              fontSize:13, fontFamily:"'Barlow',sans-serif", letterSpacing:"0.1em",
              textTransform:"uppercase", color:"rgba(255,255,255,0.3)",
            }}>loading data...</span>
          </div>
        )}

        {/* run list */}
        {data && !loading && (view === "split" || view === "runs") && (
          <motion.div variants={leftEntry} initial="hidden" animate="visible" style={{
            width: view === "split" ? "38%" : "100%",
            flexShrink:0, display:"flex", flexDirection:"column",
            borderRight: view === "split" ? "1px solid rgba(255,255,255,0.07)" : "none",
            height:"100%", overflow:"hidden",
          }}>
            {/* health strip */}
            {health && (
              <div style={{
                display:"flex", gap:22, padding:"11px 16px",
                borderBottom:"1px solid rgba(255,255,255,0.06)", flexShrink:0,
              }}>
                <Metric label="last run" value={fmtClock(health.lastRun)} />
                <Metric label="runs today" value={health.runsToday} />
                <Metric label="errors" value={health.errors}
                        color={health.errors ? "#a23978" : undefined} />
                <Metric label="runs 3+ err" value={health.badRuns}
                        color={health.badRuns ? "#c83727" : undefined} />
              </div>
            )}

            {/* filters */}
            <div style={{
              display:"flex", gap:5, flexWrap:"wrap", padding:"8px 16px",
              borderBottom:"1px solid rgba(255,255,255,0.05)", flexShrink:0,
            }}>
              {LEVELS.map(l => (
                <ChipBtn key={l} label={l} active={levelFilter === l} onClick={() => setLevelFilter(l)} />
              ))}
              <div style={{ width:1, height:16, background:"rgba(255,255,255,0.08)", margin:"0 3px" }}/>
              {PROCESSES.map(p => (
                <ChipBtn key={p} label={p.replace("_"," ")} active={procFilter === p} onClick={() => setProcFilter(p)} />
              ))}
            </div>

            {/* rows */}
            <div ref={listRef} style={{ flex:1, overflowY:"auto", scrollbarWidth:"none", position:"relative" }}>
              {!visibleRuns.length && (
                <div style={{
                  padding:"28px 16px", textAlign:"center", fontSize:13,
                  fontFamily:"'Barlow',sans-serif", color:"rgba(255,255,255,0.22)",
                }}>No runs match these filters.</div>
              )}
              {visibleRuns.map(r => {
                const s   = STATUS[r.status];
                const sel = r.runId === selectedRun;
                return (
                  <div
                    key={r.runId}
                    onClick={() => { setSelectedRun(r.runId); if (view === "runs") setView("detail"); }}
                    style={{
                      padding:"11px 16px", cursor:"pointer",
                      borderBottom:"1px solid rgba(255,255,255,0.05)",
                      borderLeft: sel ? `2px solid ${s.c}` : "2px solid transparent",
                      background: sel ? "rgba(255,255,255,0.045)" : "transparent",
                      transition:"background 0.2s",
                    }}
                  >
                    <div style={{ display:"flex", alignItems:"center", gap:9 }}>
                      <span style={{
                        fontFamily:"monospace", fontSize:13, color:"rgba(255,255,255,0.82)",
                      }}>{fmtClock(r.start)}</span>
                      <span style={{ fontSize:11, fontFamily:"monospace", color:"rgba(255,255,255,0.25)" }}>
                        {fmtDate(r.start)}
                      </span>
                      <div style={{ flex:1 }}/>
                      <Pill text={s.label} c={s.c} bg={s.bg} b={s.b} />
                    </div>

                    <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:6 }}>
                      {r.processes.map(p => (
                        <span key={p} style={{
                          fontSize:11, fontFamily:"'Barlow',sans-serif", fontWeight:600,
                          letterSpacing:"0.06em", color: PROCESS_COLOR[p] || "rgba(255,255,255,0.4)",
                        }}>{p.replace("_"," ")}</span>
                      ))}
                      <div style={{ flex:1 }}/>
                      <span style={{ fontSize:11, fontFamily:"monospace", color:"rgba(255,255,255,0.35)" }}>
                        {fmtDur(r.duration)}
                      </span>
                      <span style={{ fontSize:11, fontFamily:"monospace", color:"rgba(255,255,255,0.30)" }}>
                        {r.logs.length}L · {r.network.length}N
                      </span>
                      {r.errorCount > 0 && (
                        <span style={{ fontSize:11, fontFamily:"monospace", color:"#ef4444" }}>
                          {r.errorCount}E
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
              <GradientBar scrollEl={listRef} />
            </div>
          </motion.div>
        )}

        {/* run detail */}
        {data && !loading && (view === "split" || view === "detail") && (
          <motion.div variants={rightEntry} initial="hidden" animate="visible" style={{
            flex:1, height:"100%", display:"flex", flexDirection:"column", minWidth:0, overflow:"hidden",
          }}>
            {!active ? (
              <div style={{
                flex:1, display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:14, fontFamily:"'Barlow',sans-serif", color:"rgba(255,255,255,0.2)",
              }}>Pick a run to see its timeline.</div>
            ) : (
              <>
                {/* detail header */}
                <div style={{
                  padding:"9px 20px", flexShrink:0, display:"flex", alignItems:"center", gap:10,
                  borderBottom:"1px solid rgba(255,255,255,0.06)",
                }}>
                  <span style={{ fontSize:12, fontFamily:"monospace", color:"rgba(255,255,255,0.5)" }}>
                    {active.unlinked ? "rows without a run_id" : active.runId}
                  </span>
                  {!active.unlinked && (
                    <Tooltip title={copied ? "Copied" : "Copy run id"}>
                      <IconButton onClick={copyRunId} size="small"
                        sx={{ color:"rgba(255,255,255,0.25)", "&:hover":{ color:"#10b981" } }}>
                        {copied ? <CheckIcon sx={{ fontSize:15 }}/> : <ContentCopyIcon sx={{ fontSize:15 }}/>}
                      </IconButton>
                    </Tooltip>
                  )}
                  <div style={{ flex:1 }}/>
                  {active.trigger && (
                    <Pill text={active.trigger} c="#94a3b8" bg="rgba(148,163,184,0.09)" b="rgba(148,163,184,0.25)" />
                  )}
                  <span style={{ fontSize:12, fontFamily:"monospace", color:"rgba(255,255,255,0.4)" }}>
                    {fmtDur(active.duration)}
                  </span>
                </div>

                <div ref={detailRef} style={{
                  flex:1, overflowY:"auto", padding:"14px 20px",
                  scrollbarWidth:"none", position:"relative",
                }}>
                  {/* timeline */}
                  {activeLogs.map((l, i) => {
                    const L = lvl(l.level);
                    return (
                      <motion.div key={l.id} custom={i} variants={lineEntry} initial="hidden" animate="visible"
                        style={{ display:"flex", gap:12, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.045)" }}>
                        <span style={{
                          flexShrink:0, fontFamily:"monospace", fontSize:12,
                          color:"rgba(255,255,255,0.28)", paddingTop:2,
                        }}>{fmtClock(ts(l))}</span>
                        <Pill text={l.level} c={L.c} bg={L.bg} b={L.b} />
                        <div style={{ minWidth:0, flex:1 }}>
                          <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
                            <span style={{
                              fontSize:11, fontFamily:"'Barlow',sans-serif", fontWeight:600,
                              letterSpacing:"0.06em",
                              color: PROCESS_COLOR[l.process] || "rgba(255,255,255,0.35)",
                            }}>{l.process}</span>
                            <span style={{
                              fontSize:14, fontFamily:"'Barlow',sans-serif", fontWeight:300,
                              color:"rgba(255,255,255,0.9)", lineHeight:1.5,
                            }}>{l.message}</span>
                          </div>
                          <DetailChips details={l.details} />
                        </div>
                      </motion.div>
                    );
                  })}

                  {!activeLogs.length && (
                    <div style={{
                      padding:"20px 0", fontSize:13, fontFamily:"'Barlow',sans-serif",
                      color:"rgba(255,255,255,0.22)",
                    }}>No log lines at this filter.</div>
                  )}

                  {/* network calls */}
                  {active.network.length > 0 && (
                    <div style={{ marginTop:20, borderTop:"1px solid rgba(255,255,255,0.07)", paddingTop:12 }}>
                      <div style={{
                        fontSize:10, letterSpacing:"0.09em", textTransform:"uppercase",
                        color:"rgba(255,255,255,0.25)", fontFamily:"'Barlow',sans-serif",
                        fontWeight:500, marginBottom:8,
                      }}>Outbound calls</div>

                      {active.network.map(n => {
                        const ok = n.success !== false;
                        return (
                          <div key={n.id} style={{
                            display:"flex", alignItems:"center", gap:10, padding:"7px 0",
                            borderBottom:"1px solid rgba(255,255,255,0.045)",
                          }}>
                            <span style={{
                              flexShrink:0, fontFamily:"monospace", fontSize:12, color:"rgba(255,255,255,0.28)",
                            }}>{fmtClock(ts(n))}</span>
                            <Pill
                              text={n.status_code ?? "—"}
                              c={ok ? "#10b981" : "#ef4444"}
                              bg={ok ? "rgba(16,185,129,0.13)" : "rgba(239,68,68,0.12)"}
                              b={ok ? "rgba(16,185,129,0.4)" : "rgba(239,68,68,0.35)"}
                            />
                            <span style={{
                              fontSize:11, fontFamily:"'Barlow',sans-serif", fontWeight:600,
                              letterSpacing:"0.06em",
                              color: n.target === "ritam" ? "#fb923c" : "#a855f7",
                            }}>{n.target}</span>
                            <span style={{
                              fontFamily:"monospace", fontSize:12.5, color:"rgba(255,255,255,0.7)",
                              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                            }}>{n.method} {n.endpoint}</span>
                            <div style={{ flex:1 }}/>
                            <span style={{ fontFamily:"monospace", fontSize:12, color:"rgba(255,255,255,0.35)" }}>
                              {n.duration_ms != null ? `${n.duration_ms}ms` : "—"}
                            </span>
                          </div>
                        );
                      })}

                      {active.network.filter(n => n.error_message).map(n => (
                        <div key={`e${n.id}`} style={{
                          marginTop:8, padding:"7px 11px", fontFamily:"monospace", fontSize:12,
                          background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.25)",
                          color:"#fca5a5", whiteSpace:"pre-wrap", wordBreak:"break-word",
                        }}>{n.endpoint} — {n.error_message}</div>
                      ))}
                    </div>
                  )}

                  <GradientBar scrollEl={detailRef} />
                </div>
              </>
            )}
          </motion.div>
        )}
      </div>

      {/* ── FOOTER ── */}
      <div style={{
        position:"absolute", bottom:0, left:0, right:0, height:FOOTER_H,
        display:"flex", alignItems:"center", padding:"0 20px", gap:20,
        borderTop:"1px solid rgba(255,255,255,0.06)",
        background:"rgba(0,0,0,0.18)", zIndex:10,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:7 }}>
          <span style={{
            width:7, height:7, borderRadius:"50%", display:"inline-block", flexShrink:0,
            background: error ? "#a855f7" : data ? "#10b981" : "rgba(255,255,255,0.25)",
            boxShadow: error ? "0 0 7px #a855f7" : data ? "0 0 7px #10b981" : "none",
            transition:"background 0.3s, box-shadow 0.3s",
          }}/>
          <span style={{
            fontSize:11, fontFamily:"'Barlow',sans-serif", letterSpacing:"0.08em",
            textTransform:"uppercase",
            color: error ? "#a855f7" : "rgba(255,255,255,0.35)",
          }}>{error ? "failed" : data ? "loaded" : "waiting for key"}</span>
        </div>

        <div style={{ width:1, height:14, background:"rgba(255,255,255,0.07)" }}/>

        {data && (
          <>
            <span style={{ fontSize:11, fontFamily:"'Barlow',sans-serif", letterSpacing:"0.07em", color:"rgba(255,255,255,0.35)", textTransform:"uppercase" }}>
              runs <span style={{ color:"rgba(255,255,255,0.6)" }}>{visibleRuns.length}</span>
            </span>
            <span style={{ fontSize:11, fontFamily:"'Barlow',sans-serif", letterSpacing:"0.07em", color:"rgba(255,255,255,0.35)", textTransform:"uppercase" }}>
              logs <span style={{ color:"rgba(255,255,255,0.6)" }}>{data.counts?.logs ?? 0}</span>
            </span>
            <span style={{ fontSize:11, fontFamily:"'Barlow',sans-serif", letterSpacing:"0.07em", color:"rgba(255,255,255,0.35)", textTransform:"uppercase" }}>
              calls <span style={{ color:"rgba(255,255,255,0.6)" }}>{data.counts?.network ?? 0}</span>
            </span>
            {data.truncated && (
              <span style={{ fontSize:11, fontFamily:"monospace", color:"#fbbf24" }}>
                showing latest {data.limit}
              </span>
            )}
            <span style={{ fontSize:11, fontFamily:"monospace", color:"rgba(255,255,255,0.25)" }}>
              fetched {fmtClock(fetchedAt)}
            </span>
            {payloadSize != null && (
              <span style={{ fontSize:11, fontFamily:"monospace", color:"rgba(255,255,255,0.25)" }}>
                {(payloadSize / 1048576).toFixed(2)} MB
                <span style={{ color:"rgba(255,255,255,0.18)" }}>
                  {" "}≈{(payloadSize / 18 / 1024).toFixed(0)} KB gz
                </span>
              </span>
            )}
          </>
        )}

        <div style={{ flex:1 }}/>
        <span style={{
          fontSize:11, fontFamily:"'Barlow',sans-serif", letterSpacing:"0.1em", textTransform:"uppercase",
          background:"linear-gradient(to right,#8018e7,#10b981,#3b82f6)",
          WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", opacity:0.65,
        }}>Vision Tools · SyncLayer Logs</span>
      </div>
    </div>
  );
};

export default SyncLogs;